/**
 * Validates a URL that originates from an untrusted source (localStorage,
 * API responses, etc.) before it is used in the DOM.
 *
 * Only absolute http(s) URLs are accepted. Any other value — including
 * `javascript:` / `data:` URIs (DOM XSS, CWE-79), relative or protocol
 * malformed strings (open redirect, CWE-601), non-string inputs or empty
 * values — results in an empty string being returned.
 *
 * @param url The candidate URL from an external source.
 * @returns The original URL when it is a safe http(s) URL, otherwise `''`.
 */
export function sanitizeExternalUrl(
  url: string | ArrayBuffer | null | undefined
): string {
  if (typeof url !== 'string' || url.trim() === '') {
    return '';
  }

  // data:image/ URLs are safe for img src and CSS background-image.
  if (url.startsWith('data:image/')) {
    return url;
  }

  try {
    const parsed = new URL(url);
    // blob: URLs are origin-bound and created only from Blob/File objects — safe.
    if (
      parsed.protocol === 'http:' ||
      parsed.protocol === 'https:' ||
      parsed.protocol === 'blob:'
    ) {
      return url;
    }
  } catch {
    // Malformed URL — fall through and reject.
  }

  return '';
}
